import React from 'react'
import {IconCloseSmall, IconGallery} from '../../Assets/Icons/TinyIcons'
import {IconButton} from '../Atoms/Buttons'
import {useFileUpload} from '../../config/utils'
import {AssetData} from '../../state/rest'
import {TruncatedText} from '../Atoms/Typography'
import {Row} from '../Layout/Grid'
import {useTranslation} from 'react-i18next'
import {useAppState} from '../../state'
import {NotificationType} from '../../state/state'
import {v4 as uuid} from 'uuid'

interface UploadProps {
  onAssetSaved: (asset?: AssetData) => void
  savedAsset?: AssetData
  multiple?: boolean
  maxFiles?: number
  disabled?: boolean
}

const AssetUploaderSmall = ({onAssetSaved, savedAsset, multiple = false, maxFiles, disabled = false}: UploadProps) => {
  // TODO! Accepts only images for now
  const openFileSelect = (event: React.MouseEvent<HTMLButtonElement>) => {
    event.preventDefault()
    if (disabled) return
    // use the ref to avoid duplicate id collisions
    if (fileRef && fileRef.current) {
      fileRef.current.click()
    }
  }
  const {t} = useTranslation('assets')
  const {actions} = useAppState()
  const {loading, fileRef, handleUploadClick, startUpload} = useFileUpload({savedAsset, cb: onAssetSaved})
  const description = loading ? (
    t('assets:labels.uploading', 'Uploading...')
  ) : !savedAsset ? (
    t('assets:labels.uploadImage', 'Upload image')
  ) : (
    <TruncatedText>{savedAsset.name}</TruncatedText>
  )

  return (
    <>
      {savedAsset ? (
        <Row childMargin="1rem" align="center">
          <IconButton
            aria-label="upload photo"
            buttonProps={{onClick: e => openFileSelect(e), disabled}}
            icon={<IconGallery />}
            description={description}
          />
          <IconButton
            icon={<IconCloseSmall />}
            buttonProps={{onClick: () => !disabled && onAssetSaved(undefined), disabled}}
          />
        </Row>
      ) : (
        <Row>
          <IconButton
            aria-label="upload photo"
            buttonProps={{onClick: e => openFileSelect(e), disabled}}
            icon={<IconGallery />}
            description={description}
          />
        </Row>
      )}
      <input
        onChange={async e => {
          if (disabled) return
          if (!e.target || !e.target.files) return
          if (multiple) {
            // upload multiple files sequentially, but limit to maxFiles if provided
            const files = Array.from(e.target.files)
            const limit = typeof maxFiles === 'number' ? maxFiles : files.length

            // If the user picked too many at once, notify and only process the first N.
            if (typeof maxFiles === 'number' && files.length > maxFiles) {
              actions.addNotification({
                id: uuid(),
                type: NotificationType.ERROR,
                title: t('assets:labels.tooManyFilesTitle', 'Too many images selected'),
                description: t('assets:labels.tooManyFilesDescription', 'You can upload up to {{count}} images.', {
                  count: maxFiles
                }),
                visible: true,
                hideAfterDelay: 6000
              })
            }

            if (limit <= 0) {
              // allow selecting the same files again next time
              ;(e.target as any).value = ''
              return
            }

            const allowed = files.slice(0, Math.max(0, limit))
            for (const f of allowed) {
              await startUpload(f)
            }
          } else {
            handleUploadClick(e)
          }

          // allow selecting the same file(s) again after an upload
          ;(e.target as any).value = ''
        }}
        style={{display: 'none'}}
        type="file"
        name="fileInput"
        accept="image/*"
        multiple={multiple}
        ref={fileRef}
        disabled={disabled}
      />
    </>
  )
}

export default AssetUploaderSmall
